import { createContext, type MouseEvent, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { normaliseRoute } from './data';

interface NavigationValue {
  path: string;
  navigate: (href: string) => void;
}

const NavigationContext = createContext<NavigationValue>({ path: '/', navigate: () => undefined });

function focusDestination(element: HTMLElement | null, scroll = true) {
  if (!element) return;
  if (!element.hasAttribute('tabindex')) element.setAttribute('tabindex', '-1');
  if (scroll && typeof element.scrollIntoView === 'function') element.scrollIntoView({ behavior: 'auto', block: 'start' });
  element.focus({ preventScroll: true });
}

function focusPageStart() {
  window.scrollTo({ top: 0, behavior: 'instant' });
  focusDestination(document.getElementById('main-content'), false);
}

function destinationFromHash(hash: string) {
  try {
    return document.getElementById(decodeURIComponent(hash.slice(1)));
  } catch {
    return null;
  }
}

export function NavigationProvider({ initialPath, children }: { initialPath: string; children: ReactNode }) {
  const [path, setPath] = useState(normaliseRoute(initialPath));

  useEffect(() => {
    const priorScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    const onPopState = () => {
      setPath(normaliseRoute(window.location.pathname));
      requestAnimationFrame(() => {
        const destination = window.location.hash && destinationFromHash(window.location.hash);
        if (destination) focusDestination(destination);
        else focusPageStart();
      });
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      window.history.scrollRestoration = priorScrollRestoration;
    };
  }, []);

  const navigate = useCallback((href: string) => {
    if (href.startsWith('#')) {
      const element = document.getElementById(href.slice(1));
      focusDestination(element);
      history.replaceState(null, '', href);
      return;
    }
    const url = new URL(href, window.location.origin);
    const next = normaliseRoute(url.pathname);
    history.pushState(null, '', `${url.pathname}${url.search}${url.hash}`);
    setPath(next);
    requestAnimationFrame(() => {
      if (url.hash) focusDestination(destinationFromHash(url.hash));
      else focusPageStart();
    });
  }, []);

  const value = useMemo(() => ({ path, navigate }), [path, navigate]);
  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

export function useNavigation() {
  return useContext(NavigationContext);
}

export function Link({ href, children, className, onClick, ...rest }: {
  href: string;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  [key: string]: unknown;
}) {
  const { navigate } = useNavigation();
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (/^(?:https?:|mailto:|tel:)/i.test(href) && !href.startsWith(window.location.origin)) return;
    event.preventDefault();
    onClick?.();
    navigate(href);
  };
  return <a href={href} className={className} onClick={handleClick} {...rest}>{children}</a>;
}
